import { GoldCheck } from "@/components/GoldCheck";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Megaphone, HeartHandshake, Target, X, PartyPopper, Bell, BellRing, Download } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { toast } from "sonner";
import { BankDonate } from "@/components/donate/BankDonate";

export function FeedAnnouncements() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/announcements").then(({ data }) => setList(data)).catch(() => {}); }, []);
  const dismiss = (key) => {
    setList((l) => l.filter((a) => a.key !== key));
    api.post(`/announcements/${key}/dismiss`).catch(() => {});
  };
  if (!list.length) return null;
  return <div className="announce-card space-y-2.5">{list.map((a) => (
    <div key={a.key} className="fade-up relative overflow-hidden rounded-3xl text-white bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-400 shadow-xl shadow-pink-200" data-testid={`announcement-${a.key}`}>
      {a.image && <img src={imgSrc(a.image)} alt="" className="w-full aspect-[3/2] object-cover" data-testid={`announcement-image-${a.key}`} />}
      <div className="relative p-5 sm:p-6">
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/15" />
      <p className="relative text-[11px] uppercase tracking-wider font-bold text-white/85 flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5" /> {a.url ? "Neue Funktion" : "Ankündigung"}</p>
      <p className="relative mt-1 font-display text-xl font-extrabold" data-testid={`announcement-title-${a.key}`}>{a.title}</p>
      <p className="relative mt-1.5 text-sm text-white/95 whitespace-pre-line">{a.text}</p>
      {a.notify_me && <StoreNotify a={a} />}
      {a.paypal && <div className="relative mt-4 rounded-2xl bg-white p-3"><BankDonate testId={`announcement-bank-${a.key}`} /></div>}
      <div className="relative mt-4 flex flex-wrap gap-2">
        {a.url && <Link to={a.url} onClick={() => dismiss(a.key)} data-testid={`announcement-cta-${a.key}`} className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-white text-rose-600 text-sm font-extrabold hover:scale-[1.03] transition-transform"><Zap className="w-4 h-4" /> {a.cta}</Link>}
        <button onClick={() => dismiss(a.key)} data-testid={`announcement-ok-${a.key}`} className={`px-5 h-10 rounded-full text-sm font-bold transition-colors ${a.url || a.paypal || a.notify_me ? "bg-white/20 hover:bg-white/30 text-white" : "bg-white text-rose-600 hover:bg-white/90"}`}>Ok</button>
      </div>
      </div>
    </div>
  ))}</div>;
}

function StoreNotify({ a }) {
  const [on, setOn] = useState(a.waitlisted);
  const toggle = () => api.post("/store-waitlist").then(({ data }) => { setOn(data.waitlisted); if (data.waitlisted) toast.success("Wir sagen dir sofort Bescheid, wenn die App im Store ist 📱"); }).catch(() => toast.error("Das hat nicht geklappt"));
  if (a.store) return (
    <div className="relative mt-4 flex flex-wrap gap-2" data-testid={`announcement-store-links-${a.key}`}>
      {a.store.google_url && <a href={a.store.google_url} target="_blank" rel="noopener noreferrer" data-testid={`announcement-google-${a.key}`} className="px-5 h-11 rounded-full bg-white text-rose-600 text-sm font-extrabold inline-flex items-center gap-2"><Download className="w-4 h-4" /> Google Play</a>}
      {a.store.apple_url && <a href={a.store.apple_url} target="_blank" rel="noopener noreferrer" data-testid={`announcement-apple-${a.key}`} className="px-5 h-11 rounded-full bg-white text-rose-600 text-sm font-extrabold inline-flex items-center gap-2"><Download className="w-4 h-4" /> App Store</a>}
    </div>
  );
  return (
    <button onClick={toggle} data-testid={`announcement-notify-${a.key}`} className={`relative mt-4 w-full h-11 rounded-full text-sm font-extrabold inline-flex items-center justify-center gap-2 transition-colors ${on ? "bg-white/20 text-white border border-white/60" : "bg-white text-rose-600 hover:bg-white/90"}`}>
      {on ? <><BellRing className="w-4 h-4" /> Du wirst benachrichtigt ✓</> : <><Bell className="w-4 h-4" /> Benachrichtige mich</>}
    </button>
  );
}


const MONTH = () => new Date().toLocaleDateString("de-DE", { month: "long" });

export function SupporterGoal({ prefix }) {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/supporters/goal").then(({ data }) => setG(data)).catch(() => {}); }, []);
  if (!g?.goal) return null;
  const pct = Math.min(100, Math.round((g.count / g.goal) * 100));
  return (
    <div className="rounded-2xl bg-rose-50/60 p-3 space-y-2" data-testid={`${prefix}supporter-goal`}>
      <div className="flex items-center justify-between text-xs font-bold">
        <span className="flex items-center gap-1.5 text-rose-700"><Target className="w-3.5 h-3.5" /> Ziel im {MONTH()}</span>
        <span className="text-slate-600" data-testid={`${prefix}supporter-goal-count`}>{g.count} / {g.goal}</span>
      </div>
      <div className="h-2.5 rounded-full bg-white overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-pink-500 transition-[width] duration-1000" style={{ width: `${pct}%` }} /></div>
      <p className="text-[11px] text-slate-500">{g.count >= g.goal ? "Ziel erreicht – danke euch allen! 💖" : `Noch ${g.goal - g.count} neue Unterstützer bis zum Monatsziel`}</p>
    </div>
  );
}

const CONFETTI_COLORS = ["#F43F5E", "#FB923C", "#EC4899", "#FACC15", "#FFFFFF", "#A855F7"];
const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
    {Array.from({ length: 36 }, (_, i) => (
      <span key={i} className="confetti-piece" style={{ left: `${(i * 37) % 100}%`, background: CONFETTI_COLORS[i % CONFETTI_COLORS.length], animationDelay: `${(i % 12) * 0.18}s`, animationDuration: `${2.4 + (i % 5) * 0.35}s`, width: i % 3 ? 8 : 6, height: i % 3 ? 12 : 6 }} />
    ))}
  </div>
);

export function GoalCelebration() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/supporters/goal").then(({ data }) => data.celebrate && setG(data)).catch(() => {}); }, []);
  if (!g) return null;
  const close = () => { setG(null); api.post("/supporters/goal/celebrated").catch(() => {}); };
  return (
    <div data-urgent="1" className="fade-up relative overflow-hidden rounded-3xl p-5 sm:p-6 text-white bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-400 shadow-xl shadow-pink-200" data-testid="goal-celebration">
      <Confetti />
      <button onClick={close} data-testid="goal-celebration-close" title="Schließen" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 z-10"><X className="w-4 h-4" /></button>
      <p className="relative text-[11px] uppercase tracking-wider font-bold text-white/85 flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5" /> Monatsziel erreicht</p>
      <p className="relative mt-1 font-display text-xl font-extrabold pr-8" data-testid="goal-celebration-title">Wir haben es geschafft! 🎉</p>
      <p className="relative mt-1.5 text-sm text-white/95">{g.count} {g.count === 1 ? "neuer" : "neue"} Projekt Unterstützer im {MONTH()} – unser Ziel von {g.goal} ist erreicht. Danke an alle, die mithelfen!</p>
      <div className="relative mt-4 flex flex-wrap gap-2">
        <Link to="/community-unterstuetzen" onClick={close} data-testid="goal-celebration-cta" className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-white text-rose-600 text-sm font-extrabold hover:scale-[1.03] transition-transform"><HeartHandshake className="w-4 h-4" /> Auch mithelfen</Link>
        <button onClick={close} data-testid="goal-celebration-ok" className="px-5 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-bold transition-colors">Juhu!</button>
      </div>
    </div>
  );
}

export function SupporterThanks() {
  const [show, setShow] = useState(false);
  useEffect(() => { api.get("/supporter-thanks").then(({ data }) => setShow(data.show)).catch(() => {}); }, []);
  if (!show) return null;
  const close = () => { setShow(false); api.post("/supporter-thanks/dismiss").catch(() => {}); };
  return (
    <div data-urgent="1" className="fade-up relative overflow-hidden rounded-3xl p-5 sm:p-6 text-white bg-gradient-to-br from-orange-400 via-pink-500 to-rose-500 shadow-xl shadow-orange-200" data-testid="supporter-thanks">
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/15" />
      <button onClick={close} data-testid="supporter-thanks-close" title="Schließen" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30"><X className="w-4 h-4" /></button>
      <p className="relative text-[11px] uppercase tracking-wider font-bold text-white/85 flex items-center gap-1.5"><HeartHandshake className="w-3.5 h-3.5" /> Du bist Projekt Unterstützer</p>
      <p className="relative mt-1 font-display text-xl font-extrabold pr-8">Danke von Herzen! 💖</p>
      <p className="relative mt-1.5 text-sm text-white/95">Mit deiner Unterstützung hilfst du uns, die Community am Laufen zu halten. Als Dankeschön trägst du jetzt das Häkchen neben deinem Namen.</p>
      <button onClick={close} data-testid="supporter-thanks-ok" className="relative mt-4 px-5 h-10 rounded-full bg-white text-rose-600 text-sm font-extrabold hover:scale-[1.03] transition-transform">Gern geschehen</button>
    </div>
  );
}

